import React, { useState, useEffect } from 'react';
import {
  Grid,
  Typography,
  Box,
  Stack,
  Divider,
  Button,
  Card,
  CardContent,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { SketchPicker } from 'react-color';
import { useGetSettingsQuery, useUpdateSettingsMutation } from 'state/api';
import { useSnack, SaveSnackbar } from './settingsHelper';

const WebsiteSettings = () => {
  const theme = useTheme();
  const { data } = useGetSettingsQuery();
  const settings = data?.data || {};
  const [updateSettings, { isLoading }] = useUpdateSettingsMutation();
  const { snack, showSuccess, showError, closeSnack } = useSnack();

  const [primaryColor, setPrimaryColor] = useState(theme.palette.primary.main);
  const [secondaryColor, setSecondaryColor] = useState(theme.palette.secondary.main);
  const [headerLogo, setHeaderLogo] = useState(null);
  const [footerLogo, setFooterLogo] = useState(null);
  const [favicon, setFavicon] = useState(null);
  const [loadingGif, setLoadingGif] = useState(null);
  const [appLogo, setAppLogo] = useState(null);

  useEffect(() => {
    const w = settings.custom?.websiteSettings;
    if (!w) return;
    if (w.primaryColor !== undefined) setPrimaryColor(w.primaryColor);
    if (w.secondaryColor !== undefined) setSecondaryColor(w.secondaryColor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Handle file selection & preview
  const handleFileChange = (event, setImage) => {
    const file = event.target.files[0];
    if (file) {
      setImage(URL.createObjectURL(file));
    }
  };

  const handleSave = async () => {
    try {
      // Logo/image binaries are not persisted through the settings API; colors are stored.
      await updateSettings({
        custom: { websiteSettings: { primaryColor, secondaryColor } },
      }).unwrap();
      showSuccess();
    } catch (err) {
      showError(err?.data?.error?.message);
    }
  };

  return (
    <Grid container spacing={3}>
      {/* Color Picker Section */}
      <Grid item xs={12} md={4}>
        <Card variant="outlined" sx={{ borderColor: 'divider', bgcolor: 'background.paper' }}>
          <CardContent>
            <Typography variant="h6" color="text.primary">Website Color</Typography>

            {/* Primary Color Picker */}
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 2 }}>
              <Box sx={{ width: 50, height: 50, backgroundColor: primaryColor, borderRadius: 1 }} />
              <SketchPicker color={primaryColor} onChange={(newColor) => setPrimaryColor(newColor.hex)} />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Primary Color</Typography>

            <Divider sx={{ my: 2 }} />

            {/* Secondary Color Picker */}
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box sx={{ width: 50, height: 50, backgroundColor: secondaryColor, borderRadius: 1 }} />
              <SketchPicker color={secondaryColor} onChange={(newColor) => setSecondaryColor(newColor.hex)} />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Secondary Color</Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Logo & Image Upload Section */}
      <Grid item xs={12} md={8}>
        <Grid container spacing={2}>

          {/* File Upload Component */}
          {[
            { label: "Website Header Logo (1000 x 300 px)", state: headerLogo, setter: setHeaderLogo },
            { label: "Website Footer Logo (1000 x 300 px)", state: footerLogo, setter: setFooterLogo },
            { label: "Website Favicon (32x32 px)", state: favicon, setter: setFavicon },
            { label: "Loading Gif", state: loadingGif, setter: setLoadingGif },
            { label: "App Logo (100x60 px)", state: appLogo, setter: setAppLogo },
          ].map(({ label, state, setter }, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <Card variant="outlined" sx={{ borderColor: 'divider', bgcolor: 'background.paper' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" color="text.primary">{label}</Typography>

                  <Box sx={{ mt: 2, minHeight: 125, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {state ? (
                      <Box component="img" src={state} alt={label} sx={{ maxWidth: "100%", maxHeight: 100 }} />
                    ) : (
                      <Typography color="text.secondary">No image selected</Typography>
                    )}
                  </Box>
                  <Button variant="contained" component="label" fullWidth sx={{ mt: 1 }}>
                    Choose File
                    <Box component="input" type="file" accept="image/*" hidden onChange={(e) => handleFileChange(e, setter)} />
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}

        </Grid>
      </Grid>

      {/* Save Button */}
      <Grid item xs={12}>
        <Button variant="contained" color="primary" fullWidth onClick={handleSave} disabled={isLoading}>
          {isLoading ? 'Saving...' : 'Save Information'}
        </Button>
      </Grid>

      <SaveSnackbar snack={snack} onClose={closeSnack} />
    </Grid>
  );
};

export default WebsiteSettings;
